import { useEffect, useRef, useState } from 'react';

interface CountdownProps {
  /** Unix ms – competition start timestamp (from server via Zustand) */
  startTime: number;
  /**
   * Offset between server clock and local clock (ms).
   * Corrected time = Date.now() + serverTimeOffset.
   * Prevents clock-manipulation cheating.
   */
  serverTimeOffset: number;
  /** Called once when the countdown reaches zero */
  onComplete: () => void;
  /** Label shown above the timer. Defaults to "Start za". */
  title?: string;
  /**
   * Display precision.
   * - 'seconds' (default): HH:MM:SS — used in student waiting room.
   * - 'minutes': HH h MM min — used in admin/organizer live panel.
   *   Ticks every 60 s; last minute ticks every 1 s and shows seconds.
   */
  precision?: 'seconds' | 'minutes';
}

interface TimeLeft {
  hours: number;
  minutes: number;
  seconds: number;
}

function calcTimeLeft(startTime: number, offset: number): TimeLeft {
  const remaining = Math.max(0, startTime - (Date.now() + offset));
  const totalSeconds = Math.floor(remaining / 1000);
  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Countdown timer using server-corrected time.
 *
 * Design decisions:
 * - `useRef` tracks whether `onComplete` has been fired to guarantee it runs
 *   exactly once, even if a re-render happens at the boundary second.
 * - 'seconds' precision: 1 000 ms tick — for student waiting room.
 * - 'minutes' precision: 60 000 ms tick — for admin/organizer live panel.
 *   Last minute switches to 1 000 ms tick and shows seconds instead of "0min".
 */
export const Countdown = ({
  startTime,
  serverTimeOffset,
  onComplete,
  title = 'Start za',
  precision = 'seconds',
}: CountdownProps) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    calcTimeLeft(startTime, serverTimeOffset),
  );
  const completedRef = useRef(false);

  const tickMs =
    precision === 'minutes' && !(timeLeft.hours === 0 && timeLeft.minutes === 0)
      ? 60_000
      : 1_000;

  useEffect(() => {
    const tick = () => {
      const next = calcTimeLeft(startTime, serverTimeOffset);
      setTimeLeft(next);

      if (
        next.hours === 0 &&
        next.minutes === 0 &&
        next.seconds === 0 &&
        !completedRef.current
      ) {
        completedRef.current = true;
        onComplete();
      }
    };

    tick();
    const id = setInterval(tick, tickMs);
    return () => clearInterval(id);
  }, [startTime, serverTimeOffset, onComplete, tickMs]);

  const isUrgent =
    precision === 'minutes'
      ? timeLeft.hours === 0 && timeLeft.minutes < 30
      : timeLeft.hours === 0 && timeLeft.minutes < 5;

  if (precision === 'minutes') {
    return (
      <div className="flex flex-col items-center gap-2">
        <p className="text-sm font-medium uppercase tracking-widest text-gray-400">
          {title}
        </p>
        <div
          className={`font-mono text-5xl font-bold tabular-nums sm:text-6xl ${
            isUrgent ? 'text-competition-accent' : 'text-gray-800'
          }`}
          aria-live="polite"
          aria-atomic="true"
        >
          {timeLeft.hours > 0 ? (
            <span>{timeLeft.hours}h {pad(timeLeft.minutes)}min</span>
          ) : timeLeft.minutes > 0 ? (
            <span>{timeLeft.minutes}min</span>
          ) : (
            <span>{timeLeft.seconds}s</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <p className="text-sm font-medium uppercase tracking-widest text-gray-400">
        {title}
      </p>
      <div
        className={`flex items-center gap-1 font-mono text-5xl font-bold tabular-nums sm:text-6xl ${
          isUrgent ? 'text-competition-accent' : 'text-gray-800'
        }`}
        aria-live="polite"
        aria-atomic="true"
      >
        {timeLeft.hours > 0 && (
          <>
            <span>{pad(timeLeft.hours)}</span>
            <span className="mx-0.5 text-gray-300">:</span>
          </>
        )}
        <span>{pad(timeLeft.minutes)}</span>
        <span className="mx-0.5 text-gray-300">:</span>
        <span>{pad(timeLeft.seconds)}</span>
      </div>
    </div>
  );
};
